// =============================================================================
// Plugin Combat Page — compact executor for the browser extension FAB iframe
//
// Mounts at /plugin/combat after successful auth in /auth/plugin.
// Renders inside a 500×600 FAB iframe with no sidebar or nav chrome.
// Added to AUTH_BYPASS in App.tsx so the auth loading gate never blocks it.
// =============================================================================
import { useEffect, useState } from 'react';
import { Swords, Loader2, ChevronDown } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { initializeAuth } from '@/store/slices/authSlice';
import { apiFetch } from '@/lib/api';
import type { Character } from '../../../backend/shared/types/index';
import RoutinesTab from '@/components/Combat/RoutinesTab';

const ACTIVE_CHAR_KEY = 'darkfeld-plugin-active-character';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function PluginCombatPage() {
  const dispatch = useAppDispatch();
  const { user, isLoading: authLoading } = useAppSelector((s) => s.auth);

  const [charList, setCharList]     = useState<Array<{ characterId: string; name: string }>>([]);
  const [activeId, setActiveId]     = useState<string>('');
  const [char, setChar]             = useState<Character | null>(null);
  const [charLoading, setCharLoading] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);

  // Hydrate auth (user may have navigated directly to this path)
  useEffect(() => { dispatch(initializeAuth()); }, [dispatch]);

  // Load the user's character list once authenticated
  useEffect(() => {
    if (!user) return;
    apiFetch<{ items: Array<{ characterId: string; name: string }> }>('/characters')
      .then((r) => {
        const list = r.items ?? [];
        setCharList(list);

        // Restore the previously selected character, or fall back to the first one
        const stored = localStorage.getItem(ACTIVE_CHAR_KEY);
        const valid  = list.find((c) => c.characterId === stored);
        setActiveId(valid ? stored! : (list[0]?.characterId ?? ''));
      })
      .catch(() => {})
      .finally(() => setListLoaded(true));
  }, [user]);

  // Fetch full character data whenever the selection changes
  useEffect(() => {
    if (!activeId) return;
    localStorage.setItem(ACTIVE_CHAR_KEY, activeId);
    setCharLoading(true);
    apiFetch<Character>(`/characters/${activeId}`)
      .then((c) => setChar(c))
      .catch(() => setChar(null))
      .finally(() => setCharLoading(false));
  }, [activeId]);

  // ── Loading / unauthenticated states ──────────────────────────────────────
  if (authLoading || !listLoaded) return <PluginSpinner />;

  if (!user) return (
    <div className="h-screen bg-darkbg flex flex-col items-center justify-center gap-3 px-4 text-center">
      <Swords className="w-8 h-8 text-gold/40" />
      <p className="text-parchment-muted text-sm">Session expired.</p>
      <a href="/auth/plugin" className="text-gold underline text-sm hover:text-gold/80 transition-colors">
        Re-authenticate →
      </a>
    </div>
  );

  // ── Main layout ────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen bg-darkbg overflow-hidden">

      {/* ── Compact header bar ── */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-darkbg-border bg-darkbg-card shrink-0">
        <Swords className="w-4 h-4 text-gold/70 shrink-0" />
        <span className="font-display text-sm text-gold tracking-wide">Darkfeld</span>

        {/* Character selector — dropdown when >1 owned, static label otherwise */}
        <div className="flex-1 min-w-0 flex items-center">
          {charList.length > 1 ? (
            <div className="relative flex-1 min-w-0">
              <select
                value={activeId}
                onChange={(e) => setActiveId(e.target.value)}
                className="w-full appearance-none bg-darkbg border border-darkbg-border rounded
                           px-2 pr-6 py-0.5 text-parchment text-xs
                           focus:outline-none focus:border-gold/50 cursor-pointer"
              >
                {charList.map((c) => (
                  <option key={c.characterId} value={c.characterId}>{c.name}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2
                                     w-3 h-3 text-parchment-muted/50" />
            </div>
          ) : (
            <span className="text-xs text-parchment truncate">
              {char?.name ?? charList[0]?.name ?? '—'}
            </span>
          )}
        </div>

        {/* Connected dot */}
        <div className="w-2 h-2 rounded-full bg-success shrink-0" title="Connected to Darkfeld" />
      </div>

      {/* ── Routines tab — fills remaining height, scrolls internally ── */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pt-3">
        {charLoading ? (
          <PluginSpinner />
        ) : char ? (
          <RoutinesTab
            characterId={char.characterId}
            charName={char.name}
            gameSystem={char.ruleSystem}
            char={char}
            campaignId={char.campaignId}
            worldId={char.worldId}
            hideCharSwitcher
          />
        ) : (
          <div className="flex items-center justify-center h-full text-parchment-muted/40 text-sm">
            No character found
          </div>
        )}
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared spinner
// ---------------------------------------------------------------------------
function PluginSpinner() {
  return (
    <div className="h-screen bg-darkbg flex items-center justify-center gap-2
                    text-parchment-muted/50 text-sm">
      <Loader2 className="w-4 h-4 animate-spin" />
      Loading…
    </div>
  );
}
